// OWNER: LG-ADMIN. §10 tab 6: month-to-date spend by vendor and by search (estimate vs actual, >25% over flagged),
// the month cap, Places live checks against their cap, which keys are present (last 4 characters only), and the
// suppression list editor ("Do not prospect: existing relationships"; the product never names those companies).
import { useMemo, useState } from 'preact/hooks';
import type { SuppressionEntry, SuppressionKind } from '../../../src/shared/api-leadgen';
import { navigate } from '../app';
import { ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { ApiErrorBox, ConfirmModal, Field, inputValue, useLoad } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { OPS_TZ, ymdInTz } from '../admin/dates';
import { formatPhone } from '../admin/phone';
import { leadgenApi } from './endpoints';
import {
  STATUS_LABELS, STATUS_TONES, SUPPRESSION_DEFAULT_REASON, SUPPRESSION_KIND_LABELS, deepLinkPath, int, maskLast4, monthLabel,
  monthOptions, normalizeSuppressionValue, parseSuppressionLines, usd2, vendorLabel,
} from './logic';
import { Meter, Money, ToneChip } from './parts';

export function SpendTab(_props: Record<string, never>) {
  const months = useMemo(() => monthOptions(ymdInTz(new Date(), OPS_TZ), 12), []);
  const [month, setMonth] = useState(months[0]);
  const spend = useLoad(() => leadgenApi.spend(month), [month]);
  const s = spend.data;
  const missingRequired = (s?.keys ?? []).filter((k) => k.required && !k.present);

  return (
    <div class="stack" style="gap:14px">
      <div class="adm-toolbar">
        <Field label="Month (Toronto)">
          <select value={month} onChange={(e) => setMonth(inputValue(e))}>{months.map((m) => <option value={m}>{monthLabel(m)}</option>)}</select>
        </Field>
        <span class="grow" />
        <button class="btn btn-sm" onClick={() => void spend.reload()} disabled={spend.loading}>Refresh</button>
      </div>
      <ErrorBanner error={spend.error} />
      {spend.loading && !s ? <div class="muted">Loading spend…</div> : null}
      {s ? (
        <>
          <div class="lg-grid-2">
            <div class="card stack">
              <div class="lg-card-head"><h3>{monthLabel(s.month)}</h3><span class="lg-mono"><Money usd={s.spentUsd} /> of {usd2(s.capUsd)}</span></div>
              <Meter value={s.capUsd > 0 ? s.spentUsd / s.capUsd : null} tone={s.spentUsd > s.capUsd ? 'over' : s.spentUsd > s.capUsd * 0.8 ? 'warn' : ''} label="Month spend against the cap" />
              <div class="lg-note">Reserved plus settled ledger spend. A run whose estimate would pass the cap is refused.</div>
              <div class="row" style="justify-content:space-between;margin-top:6px">
                <span>Places live checks (drawer)</span><span class="lg-mono">{int(s.liveChecks.used)} of {int(s.liveChecks.cap)}</span>
              </div>
              <Meter value={s.liveChecks.cap > 0 ? s.liveChecks.used / s.liveChecks.cap : null} tone={s.liveChecks.used >= s.liveChecks.cap ? 'over' : ''} label="Live checks against the monthly cap" />
              <div class="lg-note">Past the cap the drawer shows the stored snapshot and the Maps link.</div>
            </div>
            <div class="card stack">
              <h3 style="margin:0">By vendor</h3>
              {s.byVendor.length ? (
                <div class="adm-table-wrap">
                  <table>
                    <thead><tr><th>Vendor</th><th class="num">Estimate</th><th class="num">Reserved</th><th class="num">Settled</th><th class="num">Actual</th></tr></thead>
                    <tbody>
                      {s.byVendor.map((v) => <tr><td>{vendorLabel(v.vendor)}</td><td class="num"><Money usd={v.estUsd} /></td><td class="num"><Money usd={v.reservedUsd} /></td><td class="num"><Money usd={v.settledUsd} /></td><td class="num"><Money usd={v.actualUsd} /></td></tr>)}
                    </tbody>
                  </table>
                </div>
              ) : <span class="lg-note">Nothing spent this month.</span>}
            </div>
          </div>

          <div class="card stack">
            <h3 style="margin:0">By search</h3>
            {s.bySearch.length ? (
              <div class="adm-table-wrap">
                <table>
                  <thead><tr><th class="num">#</th><th>Search</th><th>Status</th><th class="num">Estimate</th><th class="num">Actual</th><th /></tr></thead>
                  <tbody>
                    {s.bySearch.map((r) => (
                      <tr class="adm-clickable" onClick={() => navigate(deepLinkPath('results', r.searchId))}>
                        <td class="num">{r.searchId}</td><td>{r.label}</td><td><ToneChip tone={STATUS_TONES[r.status]}>{STATUS_LABELS[r.status]}</ToneChip></td>
                        <td class="num"><Money usd={r.estimateUsd} /></td><td class="num"><Money usd={r.actualUsd} /></td>
                        <td>{r.overEstimate25 ? <ToneChip tone="bad" title="Actual is more than 25% above the estimate: review prices.ts.">over estimate by 25%+</ToneChip> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <span class="lg-note">No searches spent anything this month.</span>}
          </div>

          <div class="card stack">
            <div class="lg-card-head"><h3>Keys</h3><span class="lg-note">Last 4 characters only. Keys are set with wrangler secret put, never here.</span></div>
            {missingRequired.length ? <div class="chip chip-bad" style="display:block;white-space:normal;padding:6px 8px">Missing: {missingRequired.map((k) => k.name).join(', ')}. The stages they unlock fail with a plain error until they're set.</div> : null}
            <div class="adm-table-wrap">
              <table>
                <thead><tr><th>Key</th><th>Status</th><th>Value</th><th>Unlocks</th></tr></thead>
                <tbody>
                  {s.keys.map((k) => (
                    <tr>
                      <td class="lg-mono">{k.name}{k.required ? '' : <span class="adm-cell-sub">optional</span>}</td>
                      <td>{k.present ? <ToneChip tone="ok">present</ToneChip> : <ToneChip tone={k.required ? 'bad' : ''}>missing</ToneChip>}</td>
                      <td class="lg-mono">{k.present ? maskLast4(k.last4) : '—'}</td>
                      <td>{k.unlocks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
      <SuppressionEditor />
    </div>
  );
}

// ---------------------------------------------------------------- suppression

function SuppressionEditor() {
  const list = useLoad(() => leadgenApi.suppression(), []);
  const [filter, setFilter] = useState('');
  const [kind, setKind] = useState<SuppressionKind>('phone');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState(SUPPRESSION_DEFAULT_REASON);
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const [removing, setRemoving] = useState<SuppressionEntry | null>(null);

  const entries = list.data ?? [];
  const norm = value.trim() ? normalizeSuppressionValue(kind, value) : null;
  const dup = norm && 'value' in norm && entries.some((e) => e.kind === kind && e.value.toLowerCase() === norm.value.toLowerCase());
  const parsed = useMemo(() => parseSuppressionLines(bulk, entries), [bulk, entries]);
  const shown = entries.filter((e) => !filter.trim() || `${e.value} ${e.reason}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const add = async (rows: Array<{ kind: SuppressionKind; value: string; reason: string }>) => {
    setBusy(true); setError(null);
    try {
      list.setData(await leadgenApi.updateSuppression({ add: rows }));
      toast(`Added ${rows.length} to the suppression list.`, 'ok');
      return true;
    } catch (e) { setError(apiErrorView(e)); return false; } finally { setBusy(false); }
  };

  return (
    <div class="card stack">
      <div class="lg-card-head">
        <h3>Do not prospect: existing relationships</h3>
        <span class="lg-note">{int(entries.length)} entries. Matching places are dropped at the pull (gate “suppressed”).</span>
      </div>
      <div class="adm-muted-box">Add the phones, domains or Google place IDs of businesses PJ's other companies already serve. Keep the reason generic (“{SUPPRESSION_DEFAULT_REASON}”): don't name the other company.</div>
      <div class="adm-toolbar" style="align-items:flex-end">
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(inputValue(e) as SuppressionKind)}>{(Object.keys(SUPPRESSION_KIND_LABELS) as SuppressionKind[]).map((k) => <option value={k}>{SUPPRESSION_KIND_LABELS[k]}</option>)}</select>
        </Field>
        <Field label="Value" hint={norm && 'value' in norm ? `Saved as ${norm.value}` : norm && 'error' in norm ? norm.error : undefined} style="min-width:240px">
          <input value={value} onInput={(e) => setValue(inputValue(e))} placeholder={kind === 'phone' ? '(416) 555-0100' : kind === 'domain' ? 'example.com' : 'ChIJ…'} />
        </Field>
        <Field label="Reason" style="min-width:220px"><input value={reason} maxLength={200} onInput={(e) => setReason(inputValue(e))} /></Field>
        <button class="btn btn-ink" disabled={busy || !norm || 'error' in norm || !!dup || !reason.trim()}
          onClick={async () => { if (norm && 'value' in norm && await add([{ kind, value: norm.value, reason: reason.trim() }])) setValue(''); }}>
          {dup ? 'Already listed' : 'Add'}
        </button>
      </div>
      <details>
        <summary style="cursor:pointer;font-size:13px">Paste many at once</summary>
        <div class="stack" style="margin-top:8px">
          <Field label="One per line: value, or value, reason" hint="The kind is read from the value: a phone number, a domain (has a dot), or else a place ID.">
            <textarea rows={5} value={bulk} onInput={(e) => setBulk(inputValue(e))} placeholder={'(905) 555-0142\nwww.example.com, existing relationship'} />
          </Field>
          {parsed.problems.length ? <ul class="adm-problems">{parsed.problems.map((p) => <li class="error">{p}</li>)}</ul> : null}
          {bulk.trim() ? <div class="lg-note">{parsed.rows.length} to add{parsed.skipped ? `, ${parsed.skipped} already listed` : ''}{parsed.rows.length ? `: ${parsed.rows.slice(0, 6).map((r) => `${SUPPRESSION_KIND_LABELS[r.kind].toLowerCase()} ${r.value}`).join(', ')}${parsed.rows.length > 6 ? '…' : ''}` : ''}.</div> : null}
          <div><button class="btn btn-sm btn-ink" disabled={busy || parsed.rows.length === 0 || parsed.problems.length > 0}
            onClick={async () => { if (await add(parsed.rows.map(({ kind: k, value: v, reason: r }) => ({ kind: k, value: v, reason: r })))) setBulk(''); }}>Add {parsed.rows.length}</button></div>
        </div>
      </details>
      <ApiErrorBox error={error} />
      <ErrorBanner error={list.error} />
      {entries.length ? (
        <>
          <input value={filter} onInput={(e) => setFilter(inputValue(e))} placeholder="Filter" aria-label="Filter the suppression list" style="max-width:260px" />
          <div class="adm-table-wrap" style="max-height:420px">
            <table>
              <thead><tr><th>Kind</th><th>Value</th><th>Reason</th><th>Added</th><th /></tr></thead>
              <tbody>
                {shown.map((e) => (
                  <tr>
                    <td>{SUPPRESSION_KIND_LABELS[e.kind]}</td>
                    <td class="lg-mono">{e.kind === 'phone' ? formatPhone(e.value) : e.value}</td>
                    <td>{e.reason}</td>
                    <td>{fmtDateTime(e.createdAt)}{e.createdByName ? <span class="adm-cell-sub">{e.createdByName}</span> : null}</td>
                    <td><button class="btn btn-sm" onClick={() => setRemoving(e)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : list.data ? <div class="lg-note">The list is empty.</div> : null}
      {removing ? (
        <ConfirmModal title="Remove from the suppression list?" danger confirmLabel="Remove"
          body={<>{SUPPRESSION_KIND_LABELS[removing.kind]} <span class="lg-mono">{removing.value}</span> will be prospected again by future searches.</>}
          onConfirm={async () => { list.setData(await leadgenApi.updateSuppression({ remove: [{ kind: removing.kind, value: removing.value }] })); toast('Removed.', 'ok'); }}
          onClose={() => setRemoving(null)} />
      ) : null}
    </div>
  );
}
